import React, { useEffect, useState } from "react";
import { useVsiAppContext } from "../stores/VsiApp";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import themeStyles from "../utils/themeStyles";

import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";
import { useSearchParams } from "react-router-dom";
import { capitalizeWord, getContextForReportName } from "../utils/Helpers";

import * as renderers from "../utils/gridFormatters";

// Very annoying but unfortunately necessary to make it work with keeping the function names post-minification
const functionMap = {
  "numberFormatter": renderers.numberFormatter,
  "dateFormatter": renderers.dateFormatter,
  "percentageBarFormatter": renderers.percentageBarFormatter,
  "moneyFormatter": renderers.moneyFormatter,
  "integerFormatter": renderers.integerFormatter,
  "deviceHealthFormatter": renderers.deviceHealthFormatter,
  "healthColorAlertFormatter": renderers.healthColorAlertFormatter,
  "removeURIEncode": renderers.removeURIEncode,
  "fractionPercentageBarFormatter": renderers.fractionPercentageBarFormatter,
  "nAForNullFormatter": renderers.nAForNullFormatter,
  "dollarFormatter": renderers.dollarFormatter,
  "growthTrendFormatter": renderers.growthTrendFormatter,
  "onlineStatusFormatter": renderers.onlineStatusFormatter,
  "cloudHealthColorAlertFormatter": renderers.cloudHealthColorAlertFormatter,
  "dateTimeFormatter": renderers.dateTimeFormatter,
  "cleanNumberFormatter": renderers.cleanNumberFormatter,
  "shortDateFormatter": renderers.shortDateFormatter,
  "fractionPercentageBarCeilingFormatter": renderers.fractionPercentageBarCeilingFormatter,
  "healthAlertFormatter": renderers.healthAlertFormatter,
  "growthFormatter": renderers.growthFormatter,
  "booleanFormatter": renderers.booleanFormatter,
  "growthTrendIconFormatter": renderers.growthTrendIconFormatter,
  "expiredDateFormatter": renderers.expiredDateFormatter,
  "nullFormatter": renderers.nullFormatter,
  "daysLeftColorFormatter": renderers.daysLeftColorFormatter,
  "redNegativeFormatter": renderers.redNegativeFormatter,
  "tenProductRound": renderers.tenProductRound,
  "capacityScoreFormatter": renderers.capacityScoreFormatter,
  "powerStateFormatter": renderers.powerStateFormatter,
  "healthFormatter": renderers.healthFormatter
};

function AutobotCheckbox({ children, path, passedDisplayName, unit, autobotInfo }) {
  const { theme, client, user, savedAutobotPieces, setSavedAutobotPieces } = useVsiAppContext();
  const [isAutobotIncluded, setIsAutobotIncluded] = useState(false);
  const [searchParams] = useSearchParams();
  const matchingConfig = user.userColConfig?.filter(config => config.path === window.location.pathname);

  let deviceName;
  const params = [];
  for (const param of searchParams.entries()) {
    if (param[0] !== "period_id") {
      params.push({
        param: param[0],
        value: param[1]
      });
    }
    let pathSplit = window.location.pathname.split("/");
    let screenPath = pathSplit[pathSplit.length - 1];
    let paramName = screenPath.split("-")[0];
    if (param[0].split("_")[param[0].split("_").length - 1] === "name"
      && (param[0].split("_")[0] === paramName || param[0] === "esx_host_name")) {
      deviceName = param[1];
    }
  }

  let pathName = path || window.location.pathname;
  if (unit || deviceName) {
    pathName = `${pathName}?${unit ? unit : ""},${deviceName ? deviceName : ""}`;
  }

  if (Array.isArray(children)) {
    children = children[0];
  }

  const getDisplayName = () => {
    if (passedDisplayName) return passedDisplayName;
    let displayName;
    if (!path) {
      React.Children.forEach(children, (child) => {
        displayName = child.type?.displayName || child.type?.name;
      });
    } else {
      displayName = children.props["children"];
    }
    return displayName;
  };

  const getColumnDefs = () => {
    let columnDefs;
    if (!path) {
      React.Children.forEach(children, (child) => {
        columnDefs = child.props?.columnDefs || [];
      });
    } else {
      columnDefs = children.props?.columnDefs || [];
    }
    return columnDefs;
  };

  // TODO: Saved autobot
  useEffect(() => {
   let componentName = getDisplayName();
   if(Object.keys((savedAutobotPieces?.[pathName]?.components) || {}).includes(componentName)) {
     setIsAutobotIncluded(true)
   } else {
     setIsAutobotIncluded(false)
   }
  }, [savedAutobotPieces, unit])

  const determineContext = (path) => {
    let reportContext = path.split("/")[path.split("/").indexOf("reports") + 1];
    return getContextForReportName(reportContext);
  };

  const convertPathToWords = (path) => {
    let screenName = capitalizeWord(path.split("/")[path.split("/").length - 1].split("?")[0]);
    let splitPath = path.split("?");
    let screenUnit;
    let context = determineContext(pathName);

    if (splitPath.length < 2) {
      screenUnit = null;
    } else {
      let params = path.split("?")[path.split("?").length - 1].split(",");
      screenUnit = capitalizeWord(params[0].replace("_", " "));
    }
    let deviceName = searchParams.get(`${path.split("/")[path.split("/").indexOf("reports") + 2]}_name`);

    if (deviceName?.split("/")?.[1]) {
      return screenName;
    }

    return `${capitalizeWord(context)} - ` + screenName + (deviceName ? (" - " + deviceName) : "") + (screenUnit ? (" - " + capitalizeWord(screenUnit.replace("_", " "))) : "");
  };

  const autobotToggle = async () => {
    setIsAutobotIncluded(!isAutobotIncluded);
    let componentName = getDisplayName();
    let columnDefs = getColumnDefs();

    // Getting saved user column configuration
    let savedConfig = [];
    if (unit) {
      savedConfig = matchingConfig.filter(config => {
        return unit.toLowerCase() === config?.params?.unit;
      });
    } else {
      savedConfig = matchingConfig;
    }

    let finalDefs;

    if (componentName === "Grid" || componentName.includes("Grid")) {
      if (savedConfig[savedConfig.length - 1]?.colState) {

        finalDefs = savedConfig[savedConfig.length - 1]?.colState.map(config => {
          for (let i = 0; i < columnDefs.length; i++) {
            if (columnDefs[i].field === config.colId) {

              return {
                ...config,
                ...columnDefs[i],
                cellRenderer: columnDefs[i]?.cellRenderer?.name || null,
                headerName: columnDefs[i].headerName,
                field: config.colId || columnDefs.colId,
                hide: config.hide || columnDefs[i].hide,
                sort: config.sort || columnDefs[i].sort
              };
            } else if (config.colId === "count") {
              return config;
            }
          }
        });
      } else {
        finalDefs = columnDefs.map(config => {
          return {
            ...config,
            cellRenderer: config?.cellRenderer?.name || null
          };
        });
      }
    }

    let lines;
    if (autobotInfo?.lines) {
      lines = autobotInfo.lines;
    } else {
      lines = children?.props?.series?.data?.map(line => {
        return line?.name;
      }) || children?.props?.series?.map(line => {
        return line?.name;
      }) || children?.props?.children?.props?.series?.map(line => {
        return line?.name;
      }) || children?.props?.categories || children?.props?.series?.map(line => {
        return line?.name;
      });
    }

    let yValue = lines;

    if (typeof autobotInfo?.secondaryMetric?.[0] === "string" && autobotInfo?.secondaryMetric?.[0]?.includes("all")) {
      lines = autobotInfo?.secondaryMetric?.[0];
      yValue = params[0].value;
    }

    let passedData;
    if (autobotInfo?.data) {
      passedData = autobotInfo.data;
    } else if (children?.props?.tag == "div" && children?.props?.children?.props) {
      passedData = children.props.children.props;
    } else {
      passedData = children?.props?.data || children?.props?.series;
    }

    let savedPiece = {
      [pathName]: {
        ...savedAutobotPieces[pathName],
        user: user._id,
        client: client._id,
        title: convertPathToWords(pathName),
        path: pathName,
        params,
        components: {
          [componentName]: {
            // Might need this back later
            // component: { key, ref, type },
            ...autobotInfo,
            graphType: componentName, // Component name (ex Grid or Stockchart)
            data: passedData, // Actual data
            config: autobotInfo?.config || children?.props?.config || children?.props?.children?.props?.config,
            title: autobotInfo?.title || children?.props?.title || children?.props?.children?.props?.title,
            categories: children?.props?.categories || children?.props?.children?.props?.categories,
            yAxisTitle: autobotInfo?.yAxisTitle || children?.props?.yAxisTitle || children?.props?.children?.props?.yAxisTitle,
            unit: unit, // first dropdown, typically (ex Capacity or Performance)
            savedColConfig: finalDefs, // Grid column definitions
            filterState: savedConfig?.[savedConfig.length - 1]?.filterState,
            secondaryMetric: autobotInfo?.secondaryMetric, // Any other units, like Metrics (History)
            yAxes: autobotInfo?.yAxes || children?.props?.config?.yAxis || children?.props?.children?.props?.config?.yAxis || children?.props?.children?.props?.yAxis, // Titles
            colors: children?.props?.config?.colors || children?.props?.children?.props?.config?.colors || children?.props?.colors,
            progressConfig: autobotInfo?.progressConfig,
            lines: lines || children?.props?.categories || children?.props?.children?.props?.categories, // This is specifically for Autobot, but it's the actual lines, like Free/Used/etc vs lines for each Device Type
            yValue: autobotInfo?.yValue || yValue,
            pieSize: autobotInfo?.pieSize
          }
        }
      }
    };

    // If false moving to true
    if (!isAutobotIncluded) {
      // If path already included
      if (savedAutobotPieces[pathName]) {
        savedPiece[pathName].components = {
          ...savedAutobotPieces[pathName].components,
          ...savedPiece[pathName].components
        };

        // try{
        //   await PutAuth(`/api/v6/admin/autobot_component/${savedAutobotPieces[pathName]._id}`, savedPiece[pathName], user.token)
        // } catch (e) {
        //   console.error(e)
        // }

        // If path not included yet
      } else {
        // try {
        //   const savedComponent = await PostAuth('/api/v6/admin/autobot_component', savedPiece[pathName], user.token);
        //   let savedComponentData = savedComponent.data.data;
        savedPiece = {
          [savedPiece[pathName].path]: {
            ...savedAutobotPieces[pathName],
            ...savedPiece[pathName]
          }
        };
        // } catch (e) {
        //   console.error(e)
        // }
      }
      // If true moving to false
    } else if (isAutobotIncluded) {
      // Remove this component from the object for this path
      let oldComponentStored = savedPiece[pathName];
      oldComponentStored.components = Object.keys(savedAutobotPieces[pathName]?.components).filter(key =>
        key !== componentName).reduce((newObj, key) => {
          newObj[key] = savedAutobotPieces[pathName].components[key];
          return newObj;
        }, {}
      );

      // If this screen has an empty components object now, remove path entirely
      if (!Object.keys(savedPiece[pathName].components).length) {
        // try{
        //   await DeleteAuth(`/api/v6/admin/autobot_component/${user._id}/${oldComponentStored._id}`, user.token);
        // } catch (e) {
        //   console.error(e)
        // }

        savedPiece = Object.keys(savedAutobotPieces).reduce((newObj, key) => {
          if (key !== pathName) {
            newObj[key] = savedAutobotPieces[key];
          }
          return newObj;
        }, {});
        // If other component that still needs to be stored for this path
      } else {
        // try {
        //   await PutAuth(`/api/v6/admin/autobot_component/${savedPiece[pathName]._id}`, savedPiece[pathName], user.token)
        // } catch (e) {
        //   console.error(e)
        // }
      }
    }

    let newPieces;
    if (savedPiece[pathName]) {
      newPieces = {
        ...savedAutobotPieces,
        [pathName]: savedPiece[pathName]
      };
    } else {
      newPieces = savedPiece;
    }
    setSavedAutobotPieces(newPieces);
  };

  return (<>
    <i
      style={{ color: !isAutobotIncluded ? themeStyles[theme].disabledBtnColor : themeStyles[theme].vsiPupil, cursor: "pointer" }}
      className={`fa-${!isAutobotIncluded ? 'regular' : 'solid'} fa-envelope${isAutobotIncluded ? "-circle-check" : ""} me-2`}
      onClick={autobotToggle} />
  </>);
}

export default withErrorBoundary(AutobotCheckbox, GeneralErrorBoundary);
